import React, { FunctionComponent, useState, useEffect, Suspense } from 'react';

import { GalleryModal } from 'common/gallery/gallery-modal/GalleryModal';
import { GalleryPreview, Props as GalleryPreviewProps } from 'common/gallery/gallery-preview/GalleryPreview';
import { ImageEditor } from 'common/image-editor/ImageEditor';
import { UploadedFileAsset } from 'common/upload/Upload';

import 'common/gallery/Gallery.scss';

export interface Props extends Omit<GalleryPreviewProps, 'activeIndex' | 'onMediaEditClick'> {
    modalTitle?: string;
    /* defaults to xl */
    modalSize?: 'sm' | 'lg' | 'xl';
}

export const Gallery: FunctionComponent<Props> = ({
    galleryData,
    displayPreview,
    height,
    width,
    wrapContent,
    overflowXContent,
    canDelete,
    displayFileNameOnHover,
    openModalOnMediaClick,
    openModalOnHandleIconClick,
    maxCount,
    highlightSelected,
    showVideoControls,
    showAudioControls,
    modalTitle,
    modalSize,
    canReOrder,
    canEditImage,
    onDelete,
    onMediaClick,
    onMediaReOrder,
    onMediaEdited,
    onMediaAltTextClick,
    mediaPathsToAltText,
    selectedSources,
}): JSX.Element => {
    const [mediaData, setMediaData] = useState(galleryData);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [activeMediaIndex, setActiveMediaIndex] = useState(0);
    const [displayImageEditor, setDisplayEditor] = useState(false);
    const [mediaEditSrc, setMediaEditSrc] = useState<string>('');
    const [mediaEditIndex, setMediaEditIndex] = useState<null | number>(null);

    useEffect(() => {
        setMediaData(galleryData);
    }, [galleryData]);

    const handleDelete = (index: number): void => {
        if (onDelete) {
            const newMediaData = [...mediaData];

            newMediaData.splice(index, 1);
            onDelete(index, newMediaData);
        }
    };

    const handleModalClose = (): void => {
        setShowModal(false);
    };

    const handleMediaClick = (index: number): void => {
        setShowModal(true);
        setActiveMediaIndex(index);
        if (onMediaClick) {
            onMediaClick(index);
        }
    };

    const handleReOrder = (files: (UploadedFileAsset | File)[]): void => {
        setMediaData(files);
        if (onMediaReOrder) {
            onMediaReOrder(files);
        }
    };

    const handleMediaEdit = (index: number): void => {
        setDisplayEditor(true);
        setMediaEditIndex(index);
        setMediaEditSrc((galleryData[index] as UploadedFileAsset).url);
    };

    const handleEdited = (file: any): void => {
        if (onMediaEdited) {
            onMediaEdited(mediaEditIndex, file);
        }
    };

    return (
        <div className="r4e-gallery">
            <Suspense fallback={null}>
                {galleryData && galleryData.length ? (
                    <GalleryPreview
                        galleryData={mediaData}
                        displayPreview={displayPreview}
                        activeIndex={activeMediaIndex}
                        height={height}
                        width={width}
                        wrapContent={wrapContent}
                        overflowXContent={overflowXContent}
                        canDelete={canDelete}
                        displayFileNameOnHover={displayFileNameOnHover}
                        openModalOnMediaClick={openModalOnMediaClick}
                        openModalOnHandleIconClick={openModalOnHandleIconClick}
                        maxCount={maxCount}
                        canReOrder={canReOrder}
                        canEditImage={canEditImage}
                        highlightSelected={highlightSelected}
                        showVideoControls={showVideoControls}
                        showAudioControls={showAudioControls}
                        onDelete={handleDelete}
                        onMediaClick={handleMediaClick}
                        onMediaReOrder={handleReOrder}
                        onMediaEditClick={handleMediaEdit}
                        onMediaAltTextClick={onMediaAltTextClick}
                        mediaPathsToAltText={mediaPathsToAltText}
                        selectedSources={selectedSources}
                    />
                ) : null}
                {showModal ? (
                    <GalleryModal
                        title={modalTitle}
                        canDelete={canDelete}
                        galleryData={mediaData}
                        activeIndex={activeMediaIndex}
                        show={showModal}
                        size={modalSize}
                        onHide={handleModalClose}
                        onDelete={handleDelete}
                    />
                ) : null}
                {displayImageEditor ? (
                    <ImageEditor
                        isActive={displayImageEditor}
                        src={mediaEditSrc}
                        onExport={handleEdited}
                        onClose={(): void => {
                            setDisplayEditor(false);
                            setMediaEditSrc('');
                        }}
                    />
                ) : null}
            </Suspense>
        </div>
    );
};
